import { useState, useEffect, useCallback } from "react"

const STORAGE_KEY = "preset_tutorial_access"
const COOKIE_NAME = "preset_tutorial_gated"
const COOKIE_EXPIRY_DAYS = 365

// Visitors can watch a couple of tutorials before we ask them to fill out
// the gating form. The slugs they've used up are tracked (not just a
// count), so re-watching one of those first couple of videos never costs
// another free view. Stored in both localStorage and a cookie (like the
// full-access grant below) — if only a cookie backed this and cookie
// writes silently failed for some visitor, every read would come back
// empty and the limit would never kick in.
const FREE_VIEWS_STORAGE_KEY = "preset_tutorial_free_views"
const FREE_VIEWS_COOKIE = "preset_tutorial_free_views"
const FREE_VIEW_LIMIT = 2

/**
 * Get cookie value by name
 */
function getCookie(name) {
  if (typeof document === "undefined") return null
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(";").shift()
  return null
}

/**
 * Set cookie with expiry
 */
function setCookie(name, value, days) {
  if (typeof document === "undefined") return
  const date = new Date()
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`
}

/**
 * Slugs of tutorials already used toward the free-view allowance — merged
 * from localStorage and the cookie, since either one can be unavailable
 * for a given visitor.
 */
function getFreeViewedSlugs() {
  const slugs = new Set()

  try {
    const raw = localStorage.getItem(FREE_VIEWS_STORAGE_KEY)
    if (raw) {
      JSON.parse(raw).forEach((slug) => {
        slugs.add(slug)
      })
    }
  } catch {
    // localStorage may be unavailable, or hold something unexpected
  }

  const cookieRaw = getCookie(FREE_VIEWS_COOKIE)
  if (cookieRaw) {
    cookieRaw
      .split(",")
      .filter(Boolean)
      .forEach((slug) => {
        slugs.add(slug)
      })
  }

  return Array.from(slugs)
}

/**
 * Record a tutorial as having used one of the free views
 */
function addFreeViewedSlug(slug) {
  const viewed = getFreeViewedSlugs()
  if (viewed.includes(slug)) return
  const updated = [...viewed, slug]

  try {
    localStorage.setItem(FREE_VIEWS_STORAGE_KEY, JSON.stringify(updated))
  } catch {
    // localStorage may be unavailable
  }

  setCookie(FREE_VIEWS_COOKIE, updated.join(","), COOKIE_EXPIRY_DAYS)
}

/**
 * Check if user has tutorial access (synchronous checks only)
 * Returns: true (has access), false (no access), or null (needs async check)
 */
function checkAccessSync() {
  if (typeof window === "undefined") return false

  // Check localStorage (fastest)
  try {
    if (localStorage.getItem(STORAGE_KEY) === "granted") {
      return true
    }
  } catch {
    // localStorage may be unavailable
  }

  // Check our cookie (cross-subdomain backup)
  if (getCookie(COOKIE_NAME) === "true") {
    return true
  }

  // Check for existing HubSpot user token (auto-unlock for known HubSpot users)
  if (window.__hsUserToken) {
    return true
  }

  // If they have hubspotutk, we need to do an async check
  if (getCookie("hubspotutk")) {
    return null // Indicates we need async check
  }

  return false
}

/**
 * Check HubSpot contact status via Netlify function
 */
async function checkHubSpotCustomer(utk) {
  try {
    const response = await fetch(
      `/.netlify/functions/check-hubspot-contact?utk=${encodeURIComponent(utk)}`
    )
    if (!response.ok) return false

    const data = await response.json()
    return data.isCustomer === true
  } catch {
    return false
  }
}

/**
 * Hook to manage tutorial video gating
 *
 * Checks multiple sources to determine access:
 * 1. localStorage (previous form submission)
 * 2. Cookie (cross-subdomain backup)
 * 3. HubSpot user token
 * 4. HubSpot contact lookup (async - checks if they're a customer)
 *
 * Visitors without full access can still watch a couple of tutorials for
 * free — see `canWatch`.
 *
 * @returns {Object} - { hasAccess: boolean, isLoading: boolean, grantAccess: () => void, canWatch: (slug: string) => boolean }
 */
function useTutorialAccess() {
  const [hasAccess, setHasAccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Dev-only helper: window.resetTutorialGate() in the browser console
  useEffect(() => {
    const isDevOrPreview =
      process.env.NODE_ENV === "development" ||
      process.env.GATSBY_IS_PREVIEW === "true"
    if (!isDevOrPreview) return
    window.resetTutorialGate = () => {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(FREE_VIEWS_STORAGE_KEY)
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      document.cookie = `hubspotutk=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      document.cookie = `${FREE_VIEWS_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      console.log("Tutorial gate reset. Refresh the page.")
    }
    return () => delete window.resetTutorialGate
  }, [])

  // Check access on mount
  useEffect(() => {
    async function checkAccess() {
      // First do synchronous checks
      const syncResult = checkAccessSync()

      if (syncResult === true) {
        setHasAccess(true)
        setIsLoading(false)
        return
      }

      if (syncResult === false) {
        setHasAccess(false)
        setIsLoading(false)
        return
      }

      // syncResult is null - need to check HubSpot API
      const utk = getCookie("hubspotutk")
      if (utk) {
        const isCustomer = await checkHubSpotCustomer(utk)
        if (isCustomer) {
          // They're a customer - grant access and remember it
          try {
            localStorage.setItem(STORAGE_KEY, "granted")
          } catch {
            // localStorage may be unavailable
          }
          setCookie(COOKIE_NAME, "true", COOKIE_EXPIRY_DAYS)
          setHasAccess(true)
        }
      }

      setIsLoading(false)
    }

    checkAccess()
  }, [])

  // Grant access (called after form submission)
  const grantAccess = useCallback(() => {
    if (typeof window === "undefined") return

    // Set localStorage
    try {
      localStorage.setItem(STORAGE_KEY, "granted")
    } catch {
      // localStorage may be unavailable
    }

    // Set cookie (1 year expiry)
    setCookie(COOKIE_NAME, "true", COOKIE_EXPIRY_DAYS)

    setHasAccess(true)
  }, [])

  // Determine (and, if it's one of the first FREE_VIEW_LIMIT distinct
  // tutorials, record) whether a given tutorial can be watched without
  // triggering the gating form.
  const canWatch = useCallback(
    (slug) => {
      if (hasAccess) return true

      const viewed = getFreeViewedSlugs()
      if (viewed.includes(slug)) return true

      if (viewed.length < FREE_VIEW_LIMIT) {
        addFreeViewedSlug(slug)
        return true
      }

      return false
    },
    [hasAccess]
  )

  return { hasAccess, isLoading, grantAccess, canWatch }
}

export default useTutorialAccess
